import React from 'react';
import { Typography, Button, Paper, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantPath } from '../../hooks/useTenantPath';
import ResponsiveLayout from './ResponsiveLayout';

/**
 * Gates the /create route to users who hold the CREATE_LISTING synthetic
 * permission.
 *
 * - Bright (RESO): CREATE_LISTING is emitted additively whenever the user
 *   holds ADD_OR_EDIT_UNPUBLISHED_LISTINGS, so this gate is transparent.
 * - mlsnow (Ocusell): CREATE_LISTING is emitted only to CAN_ENTER_DATA
 *   classes; "No Input" agents and view-only classes land on the gate.
 *
 * LISTING_ACCESS_PENDING users never reach this gate — the outer
 * ListingAccessPendingGate catches them first.
 */
const CreateListingGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { canCreateListing } = useAuth();
  const { getPath } = useTenantPath();
  const navigate = useNavigate();

  if (canCreateListing()) {
    return <>{children}</>;
  }

  return (
    <ResponsiveLayout>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          maxWidth: 600,
          textAlign: 'center',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ color: 'secondary.medium' }}
        >
          New Listings Not Available
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          Your account doesn&apos;t have permission to create new listings.
          Please reach out to your administrator if you believe this is a
          mistake.
        </Typography>
        <Stack spacing={2} direction="row" justifyContent="center">
          <Button
            variant="contained"
            onClick={() => navigate(getPath('/overview'))}
          >
            Back to Listings
          </Button>
        </Stack>
      </Paper>
    </ResponsiveLayout>
  );
};

export default CreateListingGate;
